import { toast } from "@sorcery/ui";
import {
  Button,
  Line,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useBreakpoints,
} from "@sorcery/ui";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { BiMenu, BiSortAlt2 } from "react-icons/bi";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import { GoPlusCircle } from "react-icons/go";
import { PiGridFourFill } from "react-icons/pi";
import { TbFolderPlus } from "react-icons/tb";
import { TbCrown } from "react-icons/tb";
import { FolderPreviewList } from "~/components/molecules/folder/FolderPreviewList";

import { DndContext, type DragEndEvent, pointerWithin } from "@dnd-kit/core";
import { DeckVisibilityEnum } from "@sorcery/db";
import { DeckPreviewList } from "~/components/molecules/deck/DeckPreviewList";
import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { useUser } from "~/providers/UserProvider";
import { LayoutEnum, layoutJotai } from "~/state/configJotai";
import { DeckSortEnum } from "~/types/deck";
import { deckSortOptions, visibilityOptions } from "~/utils/filters";
import { api } from "~/utils/trpc";
import { DeckBreadcrumbs } from "./DeckBreadcrumbs";

import type { Modifier } from "@dnd-kit/core";
import { foldersJotai } from "~/state/deckJotai";

export const restrictToRightEdgeOnly: Modifier = ({ transform, draggingNodeRect, windowRect }) => {
  if (!draggingNodeRect || !windowRect) {
    return transform;
  }

  const value = {
    ...transform,
  };

  if (draggingNodeRect.top + transform.y <= windowRect.top) {
    value.y = windowRect.top - draggingNodeRect.top;
  } else if (draggingNodeRect.bottom + transform.y >= windowRect.top + windowRect.height) {
    value.y = windowRect.top + windowRect.height - draggingNodeRect.bottom;
  }

  if (draggingNodeRect.right + transform.x >= windowRect.left + windowRect.width) {
    value.x = windowRect.left + windowRect.width - draggingNodeRect.right;
  }

  return value;
};

export const DecksTab = ({ isThisUser, userId }: { isThisUser: boolean; userId: string }) => {
  /************************************************************************************
   * initialization
   */
  const { isLoading: providerUserLoading } = useUser();
  const { isLg } = useBreakpoints();
  const [view, setView] = useAtom(layoutJotai);
  const [sort, setSort] = useState<DeckSortEnum>(DeckSortEnum.LATEST);
  const [visibility, setVisibility] = useState<DeckVisibilityEnum | "*">("*");
  const [avatar, setAvatar] = useState<string>("*");
  const { openModal } = useModal();
  const trpcUtils = api.useUtils();
  const [folders, setFolders] = useAtom(foldersJotai);

  const { query: routerParams } = useRouter();
  const folderId = routerParams.folder as string | undefined;

  const queryObj = useMemo(
    () => ({
      id: userId,
      avatar,
      sort,
      visibility: isThisUser ? visibility : DeckVisibilityEnum.Public,
      folderId,
    }),
    [userId, avatar, sort, visibility, isThisUser, folderId],
  );

  /************************************************************************************
   * data fetching
   */
  const { data: avatars } = api.card.getAllAvatars.useQuery(undefined, {
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const {
    data: myDecks,
    isFetching: decksFetching,
    isRefetching: decksRefetching,
  } = api.deck.getByUserId.useQuery(queryObj, {
    enabled: !providerUserLoading,
  });

  const { isFetching: foldersFetching, isRefetching: foldersRefetching } =
    api.deck.getMyFolders.useQuery(
      {
        folderId,
      },
      {
        select: (data) => {
          setFolders(data);
        },
        enabled: !!isThisUser,
      },
    );
  const {
    data: activeFolder,
    isFetching: activeFolderFetching,
    isRefetching: activeFolderRefetching,
  } = api.deck.getFolderById.useQuery(
    {
      id: folderId,
    },
    {
      enabled: !!isThisUser,
    },
  );
  const { mutateAsync: moveDeckToFolder } = api.deck.moveDeckToFolder.useMutation({
    onSuccess: () => {
      toast("Deck moved");
      void trpcUtils.deck.getByUserId.invalidate();
      void trpcUtils.deck.getMyFolders.invalidate();
    },
    onError: () => {
      toast.error("Failed to move deck");
    },
  });

  /*************************************************************************************************************************
   * react helper hooks
   */
  const loading = useMemo(
    () =>
      (decksFetching && !decksRefetching) ||
      (foldersFetching && !foldersRefetching) ||
      (activeFolderFetching && !activeFolderRefetching),
    [
      decksFetching,
      decksRefetching,
      foldersFetching,
      foldersRefetching,
      activeFolderFetching,
      activeFolderRefetching,
    ],
  );

  const avatarOptions = useMemo(() => {
    return [{ label: "Any Avatar", value: "*" }].concat(
      (avatars ?? []).map((a) => ({
        label: a.name,
        value: a.id,
      })),
    );
  }, [avatars]);

  const ViewIcon = useMemo(() => {
    return view === LayoutEnum.LIST ? BiMenu : PiGridFourFill;
  }, [view]);

  const VisibilityIcon = useMemo(() => {
    return visibility === DeckVisibilityEnum.Private ? BsEyeSlash : BsEye;
  }, [visibility]);

  const content = useMemo(() => {
    const children = loading ? (
      <div className="my-4">
        <div className="flex justify-center duration-500 animate-in fade-in lg:justify-start">
          <LoadingSpinner />
        </div>
        {/* No results */}
      </div>
    ) : (
      <>
        <div className="flex w-full flex-col justify-between pb-4 lg:flex-row lg:gap-12">
          {/* Sorting and filtering */}
          <div className="flex items-center justify-center lg:justify-start">
            {/* Avatar */}
            <span className="mr-1">
              <Select onValueChange={(v) => setAvatar(v)}>
                <SelectTrigger>
                  <TbCrown size={16} />
                  <SelectValue
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    placeholder={avatarOptions.find((a) => a.value === avatar)!.label}
                  />
                </SelectTrigger>
                <SelectContent>
                  {avatarOptions.map((a) => (
                    <SelectItem key={a.label} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </span>
            {/* Sort */}
            <span className="mr-1">
              <Select onValueChange={(v) => setSort(v as DeckSortEnum)}>
                <SelectTrigger>
                  <BiSortAlt2 size={16} />
                  <SelectValue
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    placeholder={deckSortOptions.find((a) => a.value === sort)!.label}
                  />
                </SelectTrigger>
                <SelectContent>
                  {deckSortOptions.map((a) => (
                    <SelectItem key={a.label} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </span>

            {isThisUser && (
              <>
                {/* Visibility */}
                <span className="border-r border-line-color pr-3 lg:pr-5">
                  <Select onValueChange={(v) => setVisibility(v as DeckVisibilityEnum | "*")}>
                    <SelectTrigger>
                      <VisibilityIcon size={16} />
                      <SelectValue
                        placeholder={
                          // biome-ignore lint/style/noNonNullAssertion: <explanation>
                          visibilityOptions.find((a) => a.value === visibility)!.label
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {visibilityOptions.map((a) => (
                        <SelectItem key={a.label} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </span>

                {/* Layout */}
                <div
                  className="cursor-pointer p-1 pl-2 transition duration-150 hover:opacity-60 lg:pl-4"
                  onClick={() =>
                    setView(view === LayoutEnum.LIST ? LayoutEnum.GRID : LayoutEnum.LIST)
                  }
                >
                  <span className="mr-1 hidden lg:inline">Layout</span>
                  <ViewIcon
                    key={view}
                    size={18}
                    aria-haspopup="true"
                    className="-mt-0.5 inline-block transition duration-150 ease-in-out animate-in fade-in"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Breadcrumbs */}
        {isThisUser && activeFolder && <DeckBreadcrumbs activeName={activeFolder.name} />}

        {/* Folders */}
        {isThisUser && !!folders?.length && (
          <>
            <Line className="mx-auto my-2" />
            <FolderPreviewList folders={folders} />
          </>
        )}

        {/* content */}
        <div className="duration-700 animate-in fade-in">
          <DeckPreviewList
            decks={myDecks ?? []}
            align={isLg ? "left" : "center"}
            variant={isThisUser ? "my" : "default"}
            layout={isThisUser ? view : undefined}
          />
        </div>
      </>
    );

    return isThisUser ? (
      <DndContext
        collisionDetection={pointerWithin}
        modifiers={[restrictToRightEdgeOnly]}
        onDragEnd={async (event: DragEndEvent) => {
          const { active, over } = event;
          if (over) {
            // remove deck from this folderlist instantly, so we dont have to wait for the data refetch from react-query
            trpcUtils.deck.getByUserId.setData(
              queryObj,
              myDecks?.filter((d) => d.id !== active.id),
            );
            await moveDeckToFolder({
              deckId: active.id as string,
              folderId: over.id === "*" ? null : (over.id as string),
            });
          }
        }}
      >
        {children}
      </DndContext>
    ) : (
      children
    );
  }, [
    loading,
    myDecks,
    isThisUser,
    view,
    avatar,
    sort,
    visibility,
    isLg,
    folders,
    activeFolder,
    avatarOptions,
    VisibilityIcon,
    ViewIcon,
    trpcUtils.deck.getByUserId.setData,
    moveDeckToFolder,
    queryObj,
    setView,
  ]);

  return (
    <div className="mb-8 w-full duration-700 animate-in fade-in">
      <div className="flex w-full flex-col justify-between lg:flex-row lg:gap-12">
        {/* Sorting and filtering */}
        <div className="flex items-center justify-center lg:justify-start">
          {isThisUser && (
            <>
              {/* Create deck */}
              <Button className={"mr-1"} onClick={() => openModal(ModalTypeEnum.CREATEDECK)}>
                <GoPlusCircle size={17} className="mt-[2px]" />
                <span className="pr-1">New Deck</span>
              </Button>
              {/* Create folder */}
              <Button
                className={"mr-1"}
                variant={"secondary"}
                onClick={() => openModal(ModalTypeEnum.CREATEFOLDER)}
              >
                <TbFolderPlus size={17} className="mt-[2px]" />
                <span className="pr-1">New Folder</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {isThisUser && <Line className="mx-auto my-6" />}

      {content}
    </div>
  );
};
